import React from 'react'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { LightspeedIcon, XeroIcon } from '../../SystemIcons'
import { formatNumberWithUnits } from '../../../utils/inspectorFormatters'
import {
  SystemSnapshotCard,
  type SnapshotMetric,
  type SyncFreshnessEntry,
} from './SystemSnapshotCard'

interface OutletProfileData {
  total: number
  dnu_total: number
  by_type: Record<string, { count: number; dnu: number }>
}

interface ProductStructureData {
  total_active: number
  composite: number
  variant_groups: number
  variants: number
  flat: number
}

interface SyncCountsData {
  outlets: number
  registers: number
  sales: number
  clients: number
  products: number
}

interface SalesSummaryData {
  total: number
  last_7d: number
  last_30d: number
  voided: number
  on_account_open: number
}

interface ClientsSummaryData {
  total: number
  companies: number
  individuals: number
  with_balance: number
  loyalty_enabled: number
}

interface RegistersSummaryData {
  total: number
  open: number
  closed: number
  by_outlet: Array<{ name: string; count: number }>
}

interface XeroSummaryData {
  contacts_total: number
  contacts_customers: number
  contacts_suppliers: number
  contacts_with_overdue: number
  invoices_receivable: number
  invoices_payable: number
}

interface SetupOverviewProps {
  outletProfile: OutletProfileData | null
  productStructure: ProductStructureData | null
  syncCounts: SyncCountsData | null
  syncCountsLoading: boolean
  xeroSummary?: XeroSummaryData | null
  salesSummary?: SalesSummaryData | null
  clientsSummary?: ClientsSummaryData | null
  registersSummary?: RegistersSummaryData | null
  syncFreshness?: SyncFreshnessEntry[] | null
}

const formatOutletType = (type: string): string =>
  type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const BreakdownRow: React.FC<{ count: number | string; label: string }> = ({
  count,
  label,
}) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40, textAlign: 'right' }}>
      {typeof count === 'number' ? formatNumberWithUnits(count) : count}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Stack>
)

const BreakdownColumn: React.FC<{
  title: string
  children: React.ReactNode
}> = ({ title, children }) => (
  <Grid item xs={6} sm={4}>
    <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
      {title}
    </Typography>
    {children}
  </Grid>
)

export const SetupOverview: React.FC<SetupOverviewProps> = ({
  outletProfile,
  productStructure,
  syncCounts,
  syncCountsLoading,
  xeroSummary,
  salesSummary,
  clientsSummary,
  registersSummary,
  syncFreshness,
}) => {
  const hasLsData = syncCounts || syncCountsLoading || outletProfile || productStructure
  const hasXeroData = !!xeroSummary

  if (!hasLsData && !hasXeroData) return null

  const lsSyncEntry = syncFreshness?.find((s) => s.system === 'lightspeed')
  const xeroSyncEntry = syncFreshness?.find((s) => s.system === 'xero')

  // Lightspeed headline metrics (4 tiles — Registers moved to breakdown)
  const lsMetrics: SnapshotMetric[] = [
    {
      label: 'Outlets',
      value: outletProfile
        ? String(outletProfile.total)
        : syncCounts
        ? formatNumberWithUnits(syncCounts.outlets)
        : '-',
    },
    {
      label: 'Sales',
      value: salesSummary
        ? formatNumberWithUnits(salesSummary.total)
        : syncCounts
        ? formatNumberWithUnits(syncCounts.sales)
        : '-',
    },
    {
      label: 'Products',
      value: productStructure
        ? formatNumberWithUnits(productStructure.total_active)
        : syncCounts
        ? formatNumberWithUnits(syncCounts.products)
        : '-',
    },
    {
      label: 'Clients',
      value: clientsSummary
        ? formatNumberWithUnits(clientsSummary.total)
        : syncCounts
        ? formatNumberWithUnits(syncCounts.clients)
        : '-',
    },
  ]

  // Xero headline metrics (Overdue only when > 0)
  const xeroMetrics: SnapshotMetric[] = xeroSummary
    ? [
        {
          label: 'Contacts',
          value: formatNumberWithUnits(xeroSummary.contacts_total),
        },
        {
          label: 'Invoices',
          value: formatNumberWithUnits(
            xeroSummary.invoices_receivable + xeroSummary.invoices_payable,
          ),
        },
        ...(xeroSummary.contacts_with_overdue > 0
          ? [
              {
                label: 'Overdue',
                value: String(xeroSummary.contacts_with_overdue),
                alertColor: 'warning.main',
              },
            ]
          : []),
      ]
    : []

  // Outlet breakdown rows
  const outletRows = outletProfile
    ? Object.entries(outletProfile.by_type).map(([type, data]) => ({
        count: data.count,
        label: formatOutletType(type),
      }))
    : []

  // Product breakdown rows
  const productRows: Array<{ count: number | string; label: string }> = []
  if (productStructure) {
    if (productStructure.flat > 0)
      productRows.push({ count: productStructure.flat, label: 'Standard Products' })
    if (productStructure.composite > 0)
      productRows.push({ count: productStructure.composite, label: 'Composite Products' })
    if (productStructure.variants > 0)
      productRows.push({
        count: productStructure.variants,
        label: 'Variants',
      })
    if (productStructure.variant_groups > 0)
      productRows.push({
        count: productStructure.variant_groups,
        label: 'Variant Groups',
      })
  }

  const hasLsBreakdown = !!(
    (outletProfile && outletRows.length > 0) ||
    salesSummary ||
    productRows.length > 0 ||
    clientsSummary ||
    registersSummary
  )

  const lsBreakdown = hasLsBreakdown ? (
    <Grid container spacing={2}>
      {outletProfile && outletRows.length > 0 && (
        <BreakdownColumn title="Outlets">
          {outletRows.map((row, i) => (
            <BreakdownRow key={i} count={row.count} label={row.label} />
          ))}
          {outletProfile.dnu_total > 0 && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ mt: 0.5, display: 'block' }}
            >
              {outletProfile.dnu_total} marked Do Not Use
            </Typography>
          )}
        </BreakdownColumn>
      )}
      {salesSummary && (
        <BreakdownColumn title="Sales">
          <BreakdownRow count={salesSummary.last_7d} label="Last 7 days" />
          <BreakdownRow count={salesSummary.last_30d} label="Last 30 days" />
          {salesSummary.voided > 0 && (
            <BreakdownRow count={salesSummary.voided} label="Voided" />
          )}
        </BreakdownColumn>
      )}
      {productRows.length > 0 && (
        <BreakdownColumn title="Products">
          {productRows.map((row, i) => (
            <BreakdownRow key={i} count={row.count} label={row.label} />
          ))}
        </BreakdownColumn>
      )}
      {clientsSummary && (
        <BreakdownColumn title="Clients">
          <BreakdownRow count={clientsSummary.individuals} label="Individual" />
          <BreakdownRow count={clientsSummary.companies} label="Company" />
          {clientsSummary.with_balance > 0 && (
            <BreakdownRow count={clientsSummary.with_balance} label="With Balance" />
          )}
        </BreakdownColumn>
      )}
      {registersSummary && (
        <BreakdownColumn title="Registers">
          <BreakdownRow count={registersSummary.total} label="Total" />
          <BreakdownRow count={registersSummary.open} label="Open" />
          <BreakdownRow count={registersSummary.closed} label="Closed" />
        </BreakdownColumn>
      )}
    </Grid>
  ) : undefined

  const xeroBreakdown = xeroSummary ? (
    <Grid container spacing={2}>
      <BreakdownColumn title="Contacts">
        <BreakdownRow count={xeroSummary.contacts_customers} label="Clients" />
        <BreakdownRow count={xeroSummary.contacts_suppliers} label="Suppliers" />
        {xeroSummary.contacts_with_overdue > 0 && (
          <BreakdownRow count={xeroSummary.contacts_with_overdue} label="With Overdue" />
        )}
      </BreakdownColumn>
      <BreakdownColumn title="Invoices">
        <BreakdownRow count={xeroSummary.invoices_receivable} label="Receivable" />
        <BreakdownRow count={xeroSummary.invoices_payable} label="Payable" />
      </BreakdownColumn>
    </Grid>
  ) : undefined

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={2.5}>
        {hasLsData && (
          <Grid item xs={12} md={hasXeroData ? 7 : 12}>
            <SystemSnapshotCard
              icon={<LightspeedIcon size={18} />}
              systemName="Lightspeed"
              syncEntry={lsSyncEntry}
              metrics={lsMetrics}
              metricsLoading={syncCountsLoading}
              breakdown={lsBreakdown}
            />
          </Grid>
        )}
        {hasXeroData && (
          <Grid item xs={12} md={hasLsData ? 5 : 12}>
            <SystemSnapshotCard
              icon={<XeroIcon size={18} />}
              systemName="Xero"
              syncEntry={xeroSyncEntry}
              metrics={xeroMetrics}
              breakdown={xeroBreakdown}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export default SetupOverview
